import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';

/* Willkommensmail nach dem ersten App-Login (28.09.2026): geht automatisch einmal
   je Mitglied raus (Brevo, Zustellnachweis). Abschnitte je Tarif (Tür nur mit
   Gym-Zugang, Sauna-Credits nur Unlimited). Das Upgrade-Angebot ist Werbung:
   nur mit Marketing-Einwilligung und erst, wenn es hier eingeschaltet ist. */
export default function WillkommensmailKarte() {
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [mitglied, setMitglied] = useState(null);

  useEffect(() => { api.get('/willkommensmail/einstellungen').then(r => setE(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (suche.trim().length < 2) { setTreffer([]); return; }
    const h = setTimeout(async () => {
      try { setTreffer((await api.get('/leads', { params: { search: suche.trim(), limit: 6 } })).data.leads || []); } catch { setTreffer([]); }
    }, 250);
    return () => clearTimeout(h);
  }, [suche]);

  if (!e) return null;
  const speichern = async (neu) => {
    setBusy(true);
    try { setE((await api.put('/willkommensmail/einstellungen', neu)).data); toast.success('Gespeichert'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy(false);
  };
  const vorschau = async () => {
    if (!mitglied) { toast.error('Erst ein Mitglied wählen'); return; }
    try {
      const html = (await api.get(`/willkommensmail/vorschau/${mitglied.id}`, { responseType: 'text' })).data;
      const w = window.open('', '_blank'); if (w) { w.document.open(); w.document.write(html); w.document.close(); } else toast.error('Popup blockiert');
    } catch { toast.error('Vorschau fehlgeschlagen'); }
  };
  const test = async () => {
    if (!mitglied) { toast.error('Erst ein Mitglied wählen'); return; }
    try { const r = (await api.post('/willkommensmail/test', { lead_id: mitglied.id })).data; toast.success(`Testmail an ${r.an}: ${r.status}`); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Testmail fehlgeschlagen'); }
  };
  const feld = (k, label, ph) => (
    <label className="space-y-1 text-xs"><span className="text-muted-foreground">{label}</span>
      <Input value={e[k] ?? ''} placeholder={ph} onChange={ev => setE({ ...e, [k]: ev.target.value })} className="h-8" /></label>
  );

  return (
    <Card className="border-border/60" data-testid="willkommensmail-karte">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Willkommensmail nach dem ersten App-Login</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">Geht automatisch einmal an jedes Mitglied, das sich zum ersten Mal in der App anmeldet: Check-in per QR-Code, Parkplatz, Tür mit Gym-Pass, Sauna-Credits bei Unlimited. Versand über Brevo mit Zustellnachweis.</p>
        <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="accent-marke" checked={!!e.aktiv} onChange={ev => speichern({ aktiv: ev.target.checked })} disabled={busy} /> Willkommensmail senden</label>
        <div className="rounded-md border border-border/60 p-3 space-y-2">
          <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="accent-marke" checked={!!e.upgrade_aktiv} onChange={ev => speichern({ upgrade_aktiv: ev.target.checked })} disabled={busy} /> <b>Upgrade-Angebot</b> in der Mail zeigen (nur mit Werbe-Einwilligung, nicht bei Unlimited)</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {feld('upgrade_preis', 'Preis', '100 €')}{feld('upgrade_statt', 'statt', '151,60 €')}
            {feld('upgrade_zusatz', 'Zusatz', 'pro Monat')}{feld('upgrade_bis', 'Gültig bis (leer = ohne Frist)', '31.10.2026')}
          </div>
          {feld('upgrade_tarif', 'Tarif', 'Performance Plus (Unlimited) mit 24 Monaten Laufzeit')}
          <Button size="sm" variant="outline" disabled={busy} onClick={() => speichern({ upgrade_preis: e.upgrade_preis, upgrade_statt: e.upgrade_statt, upgrade_zusatz: e.upgrade_zusatz, upgrade_bis: e.upgrade_bis, upgrade_tarif: e.upgrade_tarif })}>Angebot speichern</Button>
        </div>
        <div className="rounded-md border border-border/60 p-3 space-y-2">
          <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="accent-marke" checked={!!e.upgrade_kinder_aktiv} onChange={ev => speichern({ upgrade_kinder_aktiv: ev.target.checked })} disabled={busy} /> <b>Eltern-Angebot</b> in der Mail zeigen (nur mit Werbe-Einwilligung)</label>
          <p className="text-xs text-muted-foreground">Für Eltern mit einem Kind im 1x-Tarif: 2x pro Woche für 5 € mehr pro Woche (Laufzeit bleibt) oder Zweijahresvertrag Performance Kids für 68 € statt 80 €. Umgestellt wird am Empfang wie jeder Upsell.</p>
        </div>
        <div className="space-y-1.5">
          <Input value={suche} onChange={ev => setSuche(ev.target.value)} placeholder="Mitglied für Vorschau suchen (Tarif bestimmt die Abschnitte)" className="h-8" />
          {treffer.length > 0 && <div className="flex flex-wrap gap-1.5">{treffer.map(t => <button key={t.id} type="button" onClick={() => { setMitglied(t); setTreffer([]); setSuche(''); }} className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted">{t.name}</button>)}</div>}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-muted-foreground">{mitglied ? `Gewählt: ${mitglied.name}` : 'Kein Mitglied gewählt'}</span>
            <Button size="sm" variant="outline" className="h-7" onClick={vorschau}>Vorschau</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={test}>Testmail an mich</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
